import { useLayoutEffect, useRef, useState } from 'react';

const Reveal = ({ children, className = '', delay = 0, as: Tag = 'div' }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIsVisible(true);
      return undefined;
    }

    let shown = false;
    let observer;

    const show = () => {
      if (shown) return;
      shown = true;
      setIsVisible(true);
      observer?.disconnect();
    };

    const isInView = () => {
      const rect = element.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.bottom > 0 && rect.top < vh;
    };

    if (isInView()) {
      show();
      return undefined;
    }

    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) show();
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(element);

    const recheck = () => {
      if (isInView()) show();
    };

    const rafId = requestAnimationFrame(recheck);
    const timeoutId = window.setTimeout(recheck, 80);
    window.addEventListener('scroll', recheck, { passive: true });
    window.addEventListener('resize', recheck);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('scroll', recheck);
      window.removeEventListener('resize', recheck);
      observer?.disconnect();
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${isVisible ? 'reveal--visible' : ''} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
